
import { Share2, Facebook, Twitter, Linkedin, Instagram, MessageCircle, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Flight } from "@/types/flight";
import { formatDateTime } from "@/lib/formatters";

interface SocialShareButtonsProps {
  flight: Flight;
}

export function SocialShareButtons({ flight }: SocialShareButtonsProps) {
  const generateShareText = () => {
    const currentUrl = window.location.origin;
    return `Flight ${flight.flight_number}
From: ${flight.departure_city?.name} (${flight.departure_city?.code})
To: ${flight.arrival_city?.name} (${flight.arrival_city?.code})
Departure: ${formatDateTime(flight.scheduled_departure)}
Status: ${flight.status}
Gate: ${flight.gate || 'TBA'}

Check more flights at: ${currentUrl}`;
  };

  const shareText = generateShareText();
  const encodedText = encodeURIComponent(shareText);
  const currentUrl = encodeURIComponent(window.location.href);

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}&quote=${encodedText}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}&summary=${encodedText}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedText}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${currentUrl}&text=${encodedText}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=Flight Information&body=${encodedText}`;
        break;
      case 'instagram':
        // Instagram doesn't support direct URL sharing, so we'll copy to clipboard
        navigator.clipboard.writeText(shareText);
        alert('Flight details copied to clipboard! You can paste this in your Instagram story or post.');
        return;
      case 'tiktok':
        // TikTok doesn't support direct URL sharing, so we'll copy to clipboard
        navigator.clipboard.writeText(shareText);
        alert('Flight details copied to clipboard! You can use this text in your TikTok video.');
        return;
      case 'snapchat':
        // Snapchat doesn't support direct URL sharing, so we'll copy to clipboard
        navigator.clipboard.writeText(shareText);
        alert('Flight details copied to clipboard! You can add this to your Snapchat story.');
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0">
          <Share2 className="h-4 w-4" />
          <span className="sr-only">Share flight</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleShare('facebook')} className="cursor-pointer">
          <Facebook className="mr-2 h-4 w-4" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer">
          <Twitter className="mr-2 h-4 w-4" />
          Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('linkedin')} className="cursor-pointer">
          <Linkedin className="mr-2 h-4 w-4" />
          LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('instagram')} className="cursor-pointer">
          <Instagram className="mr-2 h-4 w-4" />
          Instagram
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('whatsapp')} className="cursor-pointer">
          <MessageCircle className="mr-2 h-4 w-4" />
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('tiktok')} className="cursor-pointer">
          <div className="mr-2 h-4 w-4 bg-black rounded-sm flex items-center justify-center">
            <span className="text-white text-xs font-bold">T</span>
          </div>
          TikTok
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('snapchat')} className="cursor-pointer">
          <div className="mr-2 h-4 w-4 bg-yellow-400 rounded-sm flex items-center justify-center">
            <span className="text-white text-xs font-bold">S</span>
          </div>
          Snapchat
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('telegram')} className="cursor-pointer">
          <Send className="mr-2 h-4 w-4" />
          Telegram
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('email')} className="cursor-pointer">
          <Mail className="mr-2 h-4 w-4" />
          Email
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
